import React, { useState, useEffect } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { loanAPI } from '../../utils/api';
import { useAuth } from '../../contexts/AuthContext';
import { useTranslation } from '../../utils/i18n';
import { ArrowLeft, Save, Send } from 'lucide-react';
import { useDialog } from '../../components/Dialog';

// Import step components (same as LoanFormPage)
import Step1Upload from './components/Step1Upload';
import Step2Bank from './components/Step2Bank';
import Step3Amount from './components/Step3Amount';
import Step4Term from './components/Step4Term';
import Step5Dates from './components/Step5Dates';
import Step6Customer from './components/Step6Customer';
import Step7Notes from './components/Step7Notes';

import './LoanFormPage.css';

const EditLoanDraftPage = () => {
  const navigate = useNavigate();
  const { id: draftId } = useParams();
  const { language } = useAuth();
  const { t } = useTranslation(language);
  const { showWarning, showSuccess, showError, showConfirm, DialogComponent } = useDialog();

  const [currentStep, setCurrentStep] = useState(1);
  const [formData, setFormData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [errors, setErrors] = useState({});
  const [showValidation, setShowValidation] = useState(false);

  const steps = [
    { number: 1, title: t('uploadDocuments'), component: Step1Upload },
    { number: 2, title: t('selectBank'), component: Step2Bank },
    { number: 3, title: t('loanAmountStep'), component: Step3Amount },
    { number: 4, title: t('loanTermStep'), component: Step4Term },
    { number: 5, title: t('paymentDates'), component: Step5Dates },
    { number: 6, title: t('customerInfo'), component: Step6Customer },
    { number: 7, title: t('notesStep'), component: Step7Notes },
  ];

  // Load draft data on mount
  useEffect(() => {
    const loadDraftData = async () => {
      try {
        setLoading(true);
        const response = await loanAPI.getOne(draftId);
        const draft = response.data?.loan || response.loan || response.data || response;

        setFormData({
          documents: draft.documents || [],
          bankId: draft.bankId || '',
          branchId: draft.branchId || '',
          amount: draft.amount || 0,
          termMonths: draft.termMonths || 12,
          interestPaymentDate: draft.interestPaymentDate || 5,
          principalPaymentDate: draft.principalPaymentDate || 5,
          paymentDate: draft.paymentDate || draft.interestPaymentDate || 5,
          customerId: draft.customerId || null,
          customerName: draft.customerName || '',
          customerPhone: draft.customerPhone || '',
          customerEmail: draft.customerEmail || '',
          customerIdNumber: draft.customerIdNumber || '',
          customerAddress: draft.customerAddress || '',
          customerCountry: draft.customerCountry || '',
          customerVisaType: draft.customerVisaType || '',
          notes: draft.notes || '',
        });
      } catch (error) {
        console.error('Failed to load draft:', error);
        showError(t('errorLoadingDraft') || 'Không thể tải bản nháp');
        navigate('/loan/list');
      } finally {
        setLoading(false);
      }
    };

    if (draftId) {
      loadDraftData();
    }
  }, [draftId]);

  // Auto-update currentStep based on scroll position
  useEffect(() => {
    const handleScroll = () => {
      const stepElements = steps.map(step => ({
        number: step.number,
        element: document.getElementById(`step-${step.number}`)
      }));

      for (let i = stepElements.length - 1; i >= 0; i--) {
        const { number, element } = stepElements[i];
        if (element) {
          const rect = element.getBoundingClientRect();
          if (rect.top <= window.innerHeight / 3) {
            setCurrentStep(number);
            break;
          }
        }
      }
    };

    window.addEventListener('scroll', handleScroll);
    return () => window.removeEventListener('scroll', handleScroll);
  }, []);

  // Validate each step
  const validateStep = (stepNumber) => {
    if (!formData) return {};
    const stepErrors = {};

    switch (stepNumber) {
      case 1:
        if (!formData.documents || formData.documents.length === 0) {
          stepErrors.documents = t('pleaseUploadDocuments');
        }
        break;
      case 2:
        if (!formData.bankId) {
          stepErrors.bankId = t('pleaseSelectBank');
        }
        break;
      case 3:
        if (!formData.amount || formData.amount < 500000) {
          stepErrors.amount = t('minimumAmount500k');
        }
        break;
      case 4:
        if (!formData.termMonths || formData.termMonths <= 0) {
          stepErrors.termMonths = t('pleaseSelectTerm');
        }
        break;
      case 5:
        if (!formData.paymentDate && (!formData.interestPaymentDate || !formData.principalPaymentDate)) {
          stepErrors.paymentDate = t('pleaseSelectPaymentDate');
        }
        break;
      case 6:
        if (!formData.customerName) {
          stepErrors.customerName = t('customerNameRequired');
        }
        if (!formData.customerPhone) {
          stepErrors.customerPhone = t('customerPhoneRequired');
        }
        if (!formData.customerId) {
          if (!formData.customerCountry) {
            stepErrors.customerCountry = t('customerCountryRequired');
          }
          if (!formData.customerVisaType) {
            stepErrors.customerVisaType = t('customerVisaTypeRequired');
          }
        }
        break;
      case 7:
        break;
      default:
        break;
    }

    return stepErrors;
  };

  const isStepValid = (stepNumber) => {
    const stepErrors = validateStep(stepNumber);
    return Object.keys(stepErrors).length === 0;
  };

  const canSubmit = () => {
    for (let i = 1; i <= 6; i++) {
      if (!isStepValid(i)) return false;
    }
    return true;
  };

  const cleanFormData = () => ({
    ...formData,
    bankId: formData.bankId || null,
    branchId: formData.branchId || null,
    customerId: formData.customerId || null,
    amount: Number(formData.amount) || 0,
    termMonths: Number(formData.termMonths) || 12,
  });

  const handleSaveDraft = async () => {
    setSaving(true);
    try {
      await loanAPI.updateDraft(draftId, { ...cleanFormData(), isDraft: true });
      showSuccess(t('draftSaved'));
      setTimeout(() => navigate('/loan/list'), 1500);
    } catch (error) {
      console.error('Failed to save draft:', error);
      showError(error.response?.data?.message || t('errorOccurred'));
    } finally {
      setSaving(false);
    }
  };

  const handleSubmit = async () => {
    if (!canSubmit()) {
      setShowValidation(true);
      showWarning(t('checkInfo'));
      return;
    }

    showConfirm(t('confirmSend'), async () => {
      setSaving(true);
      try {
        await loanAPI.create({ ...cleanFormData(), isDraft: false });
        await loanAPI.deleteDraft(draftId).catch(() => {});
        showSuccess(t('loanSentSuccess'));
        setTimeout(() => navigate('/loan/list'), 1500);
      } catch (error) {
        console.error('Failed to submit loan:', error);
        showError(error.response?.data?.message || t('errorOccurred'));
      } finally {
        setSaving(false);
      }
    });
  };

  if (loading || !formData) {
    return (
      <div className="loan-form-page">
        <div className="loading-screen">
          <div className="spinner"></div>
          <p>{t('loading')}</p>
        </div>
      </div>
    );
  }

  return (
    <div className="loan-form-page">
      <DialogComponent />
      <div className="page-header">
        <button className="btn-back" onClick={() => navigate('/loan/list')}>
          <ArrowLeft size={20} />
        </button>
        <h1 className="page-title">{t('editLoan')}</h1>
      </div>

      <div className="page-content">
        {/* Render all steps at once with validation indicators - same as LoanFormPage */}
        {steps.map((step) => {
          const StepComponent = step.component;
          const stepNumber = step.number;
          const isValid = isStepValid(stepNumber);
          const isCurrent = currentStep === stepNumber;

          // Check if all required steps (1-6) are valid
          const allRequiredStepsValid = [1, 2, 3, 4, 5, 6].every(n => isStepValid(n));

          // Hide Step 7 (Notes) until all required steps are complete
          if (stepNumber === 7 && !allRequiredStepsValid) {
            return null;
          }

          return (
            <div
              key={stepNumber}
              className={`step-section ${isCurrent ? 'step-active' : ''} ${isValid ? 'step-valid' : 'step-invalid'}`}
              id={`step-${stepNumber}`}
            >
              <div className="step-header">
                <div className="step-number-badge">
                  {isValid ? '✓' : stepNumber}
                </div>
                <h3 className="step-title">{step.title}</h3>
                {!isValid && stepNumber < currentStep && (
                  <span className="step-warning">⚠️ {t('incompleteStep')}</span>
                )}
              </div>
              <div className="step-content">
                <StepComponent
                  formData={formData}
                  setFormData={setFormData}
                  errors={showValidation && isCurrent ? errors : {}}
                />
              </div>
            </div>
          );
        })}
      </div>

      <div className="form-actions-sticky">
        <div className="form-actions-container">
          <button
            type="button"
            className="btn btn-secondary"
            onClick={handleSaveDraft}
            disabled={saving}
          >
            {saving ? (
              <>
                <span className="spinner-sm"></span>
                {t('savingDraft')}
              </>
            ) : (
              <>
                <Save size={18} />
                {t('saveDraft')}
              </>
            )}
          </button>

          <button
            type="button"
            className="btn btn-primary"
            onClick={handleSubmit}
            disabled={saving || !canSubmit()}
          >
            {saving ? (
              <>
                <span className="spinner-sm"></span>
                {t('sending')}
              </>
            ) : (
              <>
                <Send size={18} />
                {t('sendLoan')}
              </>
            )}
          </button>
        </div>
      </div>
    </div>
  );
};

export default EditLoanDraftPage;
